/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback, useRef } from 'preact/hooks';
import type { RefObject } from 'preact';
import { classNames } from '../utils/UNSAFE_classNames';
import { baseStyle, styles } from './themes/ResizableStyles.css';
import { getContainingBlockElem } from '../utils/PRIVATE_floatingUtils';
import { Rect } from '../utils/UNSAFE_flexibleComponentUtils';

type Point = {
  x: number;
  y: number;
};

enum ResizeEdge {
  NONE,
  N,
  NE,
  E,
  SE,
  S,
  SW,
  W,
  NW
}

export type ResizableConstraints = {
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
};

type ResizableProps = {
  onResizeStart?: (rect: Rect) => void;
  onResize?: (rect: Rect) => void;
  onResizeEnd?: (rect: Rect) => void;
  containerRef?: RefObject<HTMLElement>;
  constraints?: ResizableConstraints;
};

/**
 * A component that adds resize capability to a component. Fires the onResize event whenever
 * a potential resize gesture is detected. It is up to the consuming component to update its
 * position and dimensions when appropriate.
 */
export function Resizable({
  onResize,
  onResizeStart,
  onResizeEnd,
  containerRef,
  constraints
}: ResizableProps) {
  const isResizing = useRef<boolean>(false);
  const resizingEdge = useRef<ResizeEdge>(ResizeEdge.NONE);

  const startPosition = useRef<Point>();
  const initialParentClientRect = useRef<Rect>({ x: 0, y: 0, width: 0, height: 0 });
  const initialMousePosition = useRef<Point>({ x: 0, y: 0 });

  const findPos = (element: Element) => {
    let elem = element as HTMLElement;
    let curLeft = 0;
    let curTop = 0;
    if (elem.offsetParent) {
      do {
        curLeft += elem.offsetLeft;
        curTop += elem.offsetTop;
      } while ((elem = elem.offsetParent as HTMLElement));
    }

    return { left: curLeft, top: curTop };
  };

  /**
   * Constrains rect to the current containing block
   */
  const constrainRect = useCallback(
    (srcRect: Rect): Rect => {
      const elem = containerRef?.current;

      const result = {
        x: srcRect.x,
        y: srcRect.y,
        width: srcRect.width,
        height: srcRect.height
      };

      if (!elem) return result;

      const containingBlock = getContainingBlockElem(elem);

      const boundingRect = containingBlock?.getBoundingClientRect();
      const dialogRect = elem.getBoundingClientRect();

      const limitLeft = boundingRect ? boundingRect.left : 0;
      const limitTop = boundingRect ? boundingRect.top : 0;
      const limitRight = boundingRect ? boundingRect.right : window.innerWidth;
      const limitBottom = boundingRect ? boundingRect.bottom : window.innerHeight;

      // make sure the new rectangle fits in the min/max constraints and viewport limits

      if (
        (constraints?.minHeight && result.height < constraints.minHeight) ||
        (constraints?.maxHeight && result.height > constraints.maxHeight)
      ) {
        result.y = dialogRect.y;
        result.height = dialogRect.height;
      } else {
        if (result.y < limitTop) {
          result.y = limitTop;
          result.height = dialogRect.height;
        }
        if (result.y + result.height > limitBottom) {
          result.height = dialogRect.height;
        }
      }

      if (
        (constraints?.minWidth && result.width < constraints.minWidth) ||
        (constraints?.maxWidth && result.width > constraints.maxWidth)
      ) {
        result.x = dialogRect.x;
        result.width = dialogRect.width;
      } else {
        if (result.x < limitLeft) {
          result.x = limitLeft;
          result.width = dialogRect.width;
        }
        if (result.x + result.width > limitRight) {
          result.width = dialogRect.width;
        }
      }

      return result;
    },
    [containerRef, constraints]
  );

  /**
   * Calculates the new rect coordinates for a given mouse event
   */
  const getNewRect = useCallback(
    (event: MouseEvent) => {
      let left = 0;
      let width = 0;
      let top = 0;
      let height = 0;

      switch (resizingEdge.current) {
        case ResizeEdge.N:
          top = event.pageY - initialMousePosition.current.y;
          height = initialMousePosition.current.y - event.pageY;
          break;

        case ResizeEdge.E:
          width = event.pageX - initialMousePosition.current.x;
          break;

        case ResizeEdge.S:
          height = event.pageY - initialMousePosition.current.y;
          break;

        case ResizeEdge.W:
          left = event.pageX - initialMousePosition.current.x;
          width = initialMousePosition.current.x - event.pageX;
          break;

        case ResizeEdge.NE:
          top = event.pageY - initialMousePosition.current.y;
          width = event.pageX - initialMousePosition.current.x;
          height = initialMousePosition.current.y - event.pageY;
          break;

        case ResizeEdge.SE:
          width = event.pageX - initialMousePosition.current.x;
          height = event.pageY - initialMousePosition.current.y;
          break;

        case ResizeEdge.SW:
          left = event.pageX - initialMousePosition.current.x;
          width = initialMousePosition.current.x - event.pageX;
          height = event.pageY - initialMousePosition.current.y;
          break;

        case ResizeEdge.NW:
          left = event.pageX - initialMousePosition.current.x;
          top = event.pageY - initialMousePosition.current.y;
          width = initialMousePosition.current.x - event.pageX;
          height = initialMousePosition.current.y - event.pageY;
          break;
      }

      const newRect = {
        x: initialParentClientRect.current.x + left,
        y: initialParentClientRect.current.y + top,
        width: initialParentClientRect.current.width + width,
        height: initialParentClientRect.current.height + height
      };

      const newConstrainedRect = constrainRect(newRect);

      return newConstrainedRect;
    },
    [constrainRect]
  );

  /**
   * Handles mousedown events while the 'resize' mode is active.
   */
  const handleResize = useCallback(
    (event: MouseEvent) => {
      if (!isResizing.current) return;

      if (!startPosition.current) return;

      const newRect = getNewRect(event);
      onResize?.(newRect);

      event.stopPropagation();
      event.preventDefault();
    },
    [onResize, getNewRect]
  );

  /**
   * Handles mouseup event to exit the 'resize' mode.
   */
  const endResize = useCallback(
    (event: MouseEvent) => {
      isResizing.current = false;

      document.removeEventListener('mousemove', handleResize, true);
      document.removeEventListener('mouseup', endResize, true);

      const newRect = getNewRect(event);
      onResizeEnd?.(newRect);

      resizingEdge.current = ResizeEdge.NONE;

      event.preventDefault();
      event.stopPropagation();
    },
    [getNewRect, onResizeEnd, handleResize]
  );

  /**
   * Handles mousedown event to enter the 'resize mode.
   */
  const startResize = useCallback(
    (event: MouseEvent) => {
      const elem = event.currentTarget as HTMLElement;

      if (!elem || !elem.parentElement) return;

      const pos = findPos(elem.parentElement);

      isResizing.current = true;

      document.addEventListener('mousemove', handleResize, true);
      document.addEventListener('mouseup', endResize, true);

      initialParentClientRect.current = elem.parentElement.getBoundingClientRect();

      startPosition.current = { x: pos.left, y: pos.top };
      initialMousePosition.current = { x: event.pageX, y: event.pageY };

      const newRect = getNewRect(event);
      onResizeStart?.(newRect);
    },
    [getNewRect, onResizeStart, handleResize, endResize]
  );

  const startResizeN = (event: MouseEvent) => {
    startResize(event);
    if (isResizing.current) {
      resizingEdge.current = ResizeEdge.N;
    }
  };

  const startResizeE = (event: MouseEvent) => {
    startResize(event);
    if (isResizing.current) {
      resizingEdge.current = ResizeEdge.E;
    }
  };

  const startResizeS = (event: MouseEvent) => {
    startResize(event);
    if (isResizing.current) {
      resizingEdge.current = ResizeEdge.S;
    }
  };

  const startResizeW = (event: MouseEvent) => {
    startResize(event);
    if (isResizing.current) {
      resizingEdge.current = ResizeEdge.W;
    }
  };

  const startResizeNE = (event: MouseEvent) => {
    startResize(event);
    if (isResizing.current) {
      resizingEdge.current = ResizeEdge.NE;
    }
  };

  const startResizeSE = (event: MouseEvent) => {
    startResize(event);
    if (isResizing.current) {
      resizingEdge.current = ResizeEdge.SE;
    }
  };

  const startResizeSW = (event: MouseEvent) => {
    startResize(event);
    if (isResizing.current) {
      resizingEdge.current = ResizeEdge.SW;
    }
  };

  const startResizeNW = (event: MouseEvent) => {
    startResize(event);
    if (isResizing.current) {
      resizingEdge.current = ResizeEdge.NW;
    }
  };

  return (
    <>
      <div class={classNames([baseStyle, styles.handleN])} onMouseDown={startResizeN} />
      <div class={classNames([baseStyle, styles.handleE])} onMouseDown={startResizeE} />
      <div class={classNames([baseStyle, styles.handleS])} onMouseDown={startResizeS} />
      <div class={classNames([baseStyle, styles.handleW])} onMouseDown={startResizeW} />
      <div class={classNames([baseStyle, styles.handleNE])} onMouseDown={startResizeNE} />
      <div class={classNames([baseStyle, styles.handleSE])} onMouseDown={startResizeSE} />
      <div class={classNames([baseStyle, styles.handleSW])} onMouseDown={startResizeSW} />
      <div class={classNames([baseStyle, styles.handleNW])} onMouseDown={startResizeNW} />
    </>
  );
}
